import {useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import useDebounce from '../../hooks/useDebounce';
import {
    bulkUploadFaq, changeField, deleteFaq,
    initializeBulkUploadResult, initializeDeleteResult, initializeFilter, readFaqList
} from '../../modules/admin/faq';
import {readCategoryList} from '../../modules/admin/docs';
import FaqList from '../../components/admin/faq/FaqList';

export default function FaqListContainer() {
    const dispatch = useDispatch();

    const {
        keyword, categoryMainId, categorySubId, isActive, status, page,
        listResponse, listErrorResponse, pagination, listLoading,
        categoryTree, deleteResponse, deleteErrorResponse, deleteLoading,
        bulkUploadResponse, bulkUploadErrorResponse, bulkUploadLoading
    } = useSelector(({ faq, docs, loading }) => ({
        keyword: faq.keyword,
        categoryMainId: faq.categoryMainId,
        categorySubId: faq.categorySubId,
        isActive: faq.isActive,
        status: faq.status,
        page: faq.page,
        listResponse: faq.listResponse,
        listErrorResponse: faq.listErrorResponse,
        pagination: faq.pagination,
        listLoading: loading['faq/READ_FAQ_LIST'],
        categoryTree: docs.readCategoryListResponse,
        deleteResponse: faq.deleteResponse,
        deleteErrorResponse: faq.deleteErrorResponse,
        deleteLoading: loading['faq/DELETE_FAQ'],
        bulkUploadResponse: faq.bulkUploadResponse,
        bulkUploadErrorResponse: faq.bulkUploadErrorResponse,
        bulkUploadLoading: loading['faq/BULK_UPLOAD_FAQ']
    }));

    const debouncedKeyword = useDebounce(keyword, 500);

    const handleChange = (e) => {
        const { name, value } = e.target;
        dispatch(changeField({ name, value }));
        if (name !== 'page' && page !== 1) {
            dispatch(changeField({ name: 'page', value: 1 }));
        }
    };

    const handleResetFilters = () => {
        dispatch(changeField({ name: 'categoryMainId', value: null }));
        dispatch(changeField({ name: 'categorySubId', value: null }));
        dispatch(changeField({ name: 'isActive', value: null }));
        dispatch(changeField({ name: 'status', value: null }));
        if (page !== 1) {
            dispatch(changeField({ name: 'page', value: 1 }));
        }
    };

    useEffect(() => {
        dispatch(initializeFilter());
    }, [dispatch]);

    useEffect(() => {
        if (!categoryTree) {
            dispatch(readCategoryList());
        }
    }, [dispatch, categoryTree]);

    useEffect(() => {
        dispatch(readFaqList({
            keyword: debouncedKeyword ? debouncedKeyword.trim() : null,
            categoryMainId: categoryMainId || null,
            categorySubId: categorySubId || null,
            isActive: isActive,
            status: status || null,
            page
        }));
    }, [dispatch, debouncedKeyword, categoryMainId, categorySubId, isActive, status, page]);

    useEffect(() => {
        if (!deleteResponse) return;
        const isLastItemOnNonFirstPage =
            (pagination?.contentLength ?? 0) <= 1 && page > 1;
        if (isLastItemOnNonFirstPage) {
            dispatch(changeField({ name: 'page', value: page - 1 }));
        } else {
            dispatch(readFaqList({
                keyword: debouncedKeyword ? debouncedKeyword.trim() : null,
                categoryMainId: categoryMainId || null,
                categorySubId: categorySubId || null,
                isActive: isActive,
                status: status || null,
                page
            }));
        }
        dispatch(initializeDeleteResult());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deleteResponse]);

    const handleDeleteFaq = (id) => {
        dispatch(deleteFaq({ id }));
    };

    const handleDismissDeleteError = () => {
        dispatch(initializeDeleteResult());
    };

    const handleRefreshList = () => {
        dispatch(readFaqList({
            keyword: debouncedKeyword ? debouncedKeyword.trim() : null,
            categoryMainId: categoryMainId || null,
            categorySubId: categorySubId || null,
            isActive: isActive,
            status: status || null,
            page
        }));
    };

    const handleBulkUpload = (file) => {
        dispatch(bulkUploadFaq({ file }));
    };

    const handleDismissBulkUploadResult = () => {
        dispatch(initializeBulkUploadResult());
    };

    useEffect(() => {
        if (!bulkUploadResponse) return;
        if (page !== 1) {
            dispatch(changeField({ name: 'page', value: 1 }));
        } else {
            dispatch(readFaqList({
                keyword: debouncedKeyword ? debouncedKeyword.trim() : null,
                categoryMainId: categoryMainId || null,
                categorySubId: categorySubId || null,
                isActive: isActive,
                status: status || null,
                page
            }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bulkUploadResponse]);

    return (
        <FaqList
            keyword={keyword}
            categoryMainId={categoryMainId}
            categorySubId={categorySubId}
            isActive={isActive}
            status={status}
            page={page}
            listResponse={listResponse}
            listErrorResponse={listErrorResponse}
            pagination={pagination}
            listLoading={listLoading}
            categoryTree={categoryTree}
            handleChange={handleChange}
            handleResetFilters={handleResetFilters}
            onDeleteFaq={handleDeleteFaq}
            deleteLoading={deleteLoading}
            deleteErrorResponse={deleteErrorResponse}
            onDismissDeleteError={handleDismissDeleteError}
            onRefreshList={handleRefreshList}
            onBulkUpload={handleBulkUpload}
            bulkUploadLoading={bulkUploadLoading}
            bulkUploadResponse={bulkUploadResponse}
            bulkUploadErrorResponse={bulkUploadErrorResponse}
            onDismissBulkUploadResult={handleDismissBulkUploadResult}
        />
    );
}
